import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Alert, Badge, Button, Card, Input, Select, Tabs } from '@dreamtree-org/twreact-ui';
import PageShell from '@/components/layout/PageShell';
import PageHeader from '@/components/layout/PageHeader';
import { platformGet, platformSend, errorOf } from './platformApi';
import { usePlatform } from './PlatformSession';
import { STATUS_VARIANT } from './PlatformCompanies';
import CompanySettingsTab from './CompanySettingsTab';
import ProvisioningPanel from './ProvisioningPanel';

/**
 * P05/P06/P09 — one company: profile and lifecycle, branches, verified domains, and the
 * database connection (write-only: the URL goes in, only a redacted summary comes out).
 * Sensitive actions go through `run`, which prompts for a step-up code when the server asks.
 */

const NEXT_STATUSES = {
  active: ['suspended', 'maintenance', 'archived'],
  suspended: ['active', 'archived'],
  maintenance: ['active', 'suspended'],
  draft: ['archived'],
  provisioning_failed: ['archived'],
};

function useAction(setErr, setNotice, reload) {
  const { run } = usePlatform();
  return async (fn, notice) => {
    setErr('');
    setNotice('');
    try {
      const result = await run(fn);
      if (notice) setNotice(typeof notice === 'function' ? notice(result) : notice);
      await reload();
      return result;
    } catch (ex) {
      if (!ex.cancelled) setErr(errorOf(ex).message);
      return null;
    }
  };
}

function ProfileTab({ company, act }) {
  const [form, setForm] = useState({ displayName: company.displayName, slug: company.slug });
  const [nextStatus, setNextStatus] = useState('');
  const [reason, setReason] = useState('');
  const options = NEXT_STATUSES[company.status] || [];
  return (
    <div className="grid gap-3 md:grid-cols-2">
      <Card className="p-3 space-y-3">
        <h2 className="font-semibold">Profile</h2>
        <Input label="Company name" value={form.displayName} onChange={(e) => setForm({ ...form, displayName: e.target.value })} />
        <Input label="Sign-in address (slug)" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase() })} />
        <p className="text-xs text-secondary-500">Changing the slug moves the company&apos;s sign-in address and needs a fresh authenticator code.</p>
        <Button size="sm" onClick={() => act(() => platformSend('patch', `/companies/${company.id}`, form), 'Saved')}>Save</Button>
      </Card>
      <Card className="p-3 space-y-3">
        <h2 className="font-semibold">Lifecycle</h2>
        <p className="text-sm">Current status: <Badge size="sm" variant={STATUS_VARIANT[company.status]}>{company.status.replace('_', ' ')}</Badge></p>
        {['draft', 'provisioning', 'provisioning_failed'].includes(company.status) && (
          <Alert variant="info">A company becomes active only when provisioning finishes — it cannot be switched on by hand.</Alert>
        )}
        {options.length > 0 && (
          <>
            <Select label="Move to" value={nextStatus} onChange={setNextStatus} options={[{ value: '', label: 'Choose…' }, ...options.map((s) => ({ value: s, label: s }))]} />
            <Input label="Reason (recorded in the audit log)" value={reason} onChange={(e) => setReason(e.target.value)} />
            <Button
              size="sm"
              variant="secondary"
              disabled={!nextStatus || !reason.trim()}
              onClick={() => act(() => platformSend('post', `/companies/${company.id}/status`, { status: nextStatus, reason }), `Moved to ${nextStatus}`)}
            >
              Change status
            </Button>
          </>
        )}
      </Card>
    </div>
  );
}

function BranchesTab({ company, act }) {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  return (
    <div className="space-y-3">
      <Card className="p-3">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-secondary-500"><th className="py-1">Code</th><th>Name</th><th>Status</th><th /></tr></thead>
          <tbody>
            {company.branches.map((b) => (
              <tr key={b.id} className="border-t border-secondary-100 dark:border-secondary-800">
                <td className="py-1 font-mono">{b.code}</td>
                <td>{b.name}{b.id === company.defaultBranchId && <Badge size="sm" variant="primary" className="ml-2">default</Badge>}</td>
                <td><Badge size="sm" variant={b.status === 'active' ? 'success' : 'secondary'}>{b.status}</Badge></td>
                <td className="text-right">
                  {b.id !== company.defaultBranchId && b.status === 'active' && (
                    <Button size="sm" variant="secondary" onClick={() => act(() => platformSend('patch', `/companies/${company.id}/branches/${b.id}`, { status: 'suspended' }), `${b.code} suspended`)}>Suspend</Button>
                  )}
                  {b.status === 'suspended' && (
                    <Button size="sm" variant="secondary" onClick={() => act(() => platformSend('patch', `/companies/${company.id}/branches/${b.id}`, { status: 'active' }), `${b.code} reactivated`)}>Reactivate</Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
      <Card className="p-3 space-y-2">
        <h2 className="font-semibold">Add a branch</h2>
        <div className="grid gap-2 sm:grid-cols-3 items-end">
          <Input label="Code" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="BLR" />
          <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Bengaluru" />
          <Button
            disabled={!code || !name}
            onClick={async () => {
              const r = await act(() => platformSend('post', `/companies/${company.id}/branches`, { code, name }), (b) => `${b.code} created${b.status === 'provisioning' ? ' — it activates once the company database has it' : ''}`);
              if (r) { setCode(''); setName(''); }
            }}
          >
            Add branch
          </Button>
        </div>
      </Card>
    </div>
  );
}

function DomainsTab({ company, act }) {
  const [hostname, setHostname] = useState('');
  const [instructions, setInstructions] = useState(null);
  return (
    <div className="space-y-3">
      <Card className="p-3 space-y-2">
        <h2 className="font-semibold">Custom domains</h2>
        {company.domains.length === 0 && <p className="text-sm text-secondary-500">None. Users sign in at the company&apos;s slug address.</p>}
        {company.domains.map((d) => (
          <div key={d.id} className="flex flex-wrap items-center gap-2 text-sm border-t border-secondary-100 dark:border-secondary-800 pt-2">
            <code>{d.hostname}</code>
            <Badge size="sm" variant={d.status === 'active' ? 'success' : 'warning'}>{d.status === 'active' ? 'verified' : 'unverified'}</Badge>
            <div className="ml-auto flex gap-2">
              {d.status !== 'active' && <Button size="sm" variant="secondary" onClick={() => act(() => platformSend('post', `/companies/${company.id}/domains/${d.id}/verify`), 'Verified')}>Verify now</Button>}
              <Button size="sm" variant="secondary" onClick={() => act(() => platformSend('delete', `/companies/${company.id}/domains/${d.id}`), 'Removed')}>Remove</Button>
            </div>
          </div>
        ))}
      </Card>
      <Card className="p-3 space-y-2">
        <div className="grid gap-2 sm:grid-cols-[1fr_auto] items-end">
          <Input label="Hostname" value={hostname} onChange={(e) => setHostname(e.target.value)} placeholder="agents.example.com" />
          <Button
            disabled={!hostname}
            onClick={async () => {
              const r = await act(() => platformSend('post', `/companies/${company.id}/domains`, { hostname }));
              if (r) { setInstructions(r); setHostname(''); }
            }}
          >
            Add domain
          </Button>
        </div>
        {instructions && (
          <Alert variant="info" title="Prove ownership with a DNS TXT record">
            Create <code>{instructions.txtName}</code> with the value <code className="break-all">{instructions.txtValue}</code>, then press Verify now. The value is shown only here.
          </Alert>
        )}
      </Card>
    </div>
  );
}

function lastCheckText(db) {
  if (!db.lastCheckAt) return 'never';
  const outcome = db.lastCheckOk ? 'OK' : 'failed';
  return `${outcome} · ${new Date(db.lastCheckAt).toLocaleString()}`;
}

function DatabaseTab({ company, act, onChanged }) {
  const [url, setUrl] = useState('');
  const db = company.database;
  return (
    <div className="grid gap-3 md:grid-cols-2">
      <Card className="p-3 space-y-2">
        <h2 className="font-semibold">Connection</h2>
        {!db ? <p className="text-sm text-secondary-500">No database attached yet.</p> : (
          <dl className="text-sm grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
            <dt className="text-secondary-500">Target</dt><dd><code className="break-all">{db.target}</code></dd>
            <dt className="text-secondary-500">Mode</dt><dd>{db.mode}</dd>
            <dt className="text-secondary-500">Status</dt><dd>{db.status}</dd>
            <dt className="text-secondary-500">Version</dt><dd>{db.connectionVersion} · key {db.keyId}</dd>
            <dt className="text-secondary-500">Schema</dt><dd>{db.schemaVersion || 'not migrated'}</dd>
            <dt className="text-secondary-500">Last check</dt>
            <dd>{lastCheckText(db)}</dd>
          </dl>
        )}
        {db && db.lastCheckError && <Alert variant="error">{db.lastCheckError}</Alert>}
        {db && <Button size="sm" variant="secondary" onClick={() => act(() => platformSend('post', `/companies/${company.id}/database/test`), (r) => (r.ok ? 'Connection OK' : 'Connection failed — see the error'))}>Test connection</Button>}
      </Card>
      <Card className="p-3 space-y-2">
        <h2 className="font-semibold">{db ? 'Replace / rotate' : 'Attach an existing database'}</h2>
        <p className="text-xs text-secondary-500">Write-only: the URL is encrypted on the server and never shown again — only the redacted target. Needs a fresh authenticator code.</p>
        <Input
          label="mysql:// URL"
          type="password"
          // "new-password" (not "off", which Chrome ignores) keeps a saved login from being
          // autofilled into this field and silently prefixed to the URL.
          autoComplete="new-password"
          data-1p-ignore
          data-lpignore="true"
          name="company-db-url"
          value={url} onChange={(e) => setUrl(e.target.value)} placeholder="mysql://user:password@host:3306/database" />
        <Button
          size="sm"
          disabled={!url}
          onClick={async () => {
            const r = await act(() => platformSend('put', `/companies/${company.id}/database`, { url }), (res) => (res.rotated ? 'Rotated — existing connections drain' : 'Stored'));
            setUrl('');
            return r;
          }}
        >
          Save connection
        </Button>
      </Card>
      <ProvisioningPanel company={company} act={act} onChanged={onChanged} />
    </div>
  );
}

export default function PlatformCompanyDetail() {
  const { id } = useParams();
  const [company, setCompany] = useState(null);
  const [err, setErr] = useState('');
  const [notice, setNotice] = useState('');

  const reload = useCallback(async () => {
    try {
      setCompany(await platformGet(`/companies/${id}`));
    } catch (ex) {
      setErr(errorOf(ex).message);
    }
  }, [id]);

  useEffect(() => { reload(); }, [reload]);
  const act = useAction(setErr, setNotice, reload);

  if (!company) return <PageShell>{err ? <Alert variant="error">{err}</Alert> : null}</PageShell>;

  return (
    <PageShell className="space-y-3">
      <PageHeader
        title={company.displayName}
        backTo="/platform/companies"
        backLabel="Companies"
        meta={<Badge size="sm" variant={STATUS_VARIANT[company.status]}>{company.status.replace('_', ' ')}</Badge>}
        subtitle={<code className="text-xs">{company.slug}</code>}
      />
      {err && <Alert variant="error" dismissible onDismiss={() => setErr('')}>{err}</Alert>}
      {notice && <Alert variant="success" dismissible onDismiss={() => setNotice('')}>{notice}</Alert>}
      <Tabs defaultIndex={0} variant="line" size="sm">
        <Tabs.List>
          <Tabs.Tab index={0}>Profile</Tabs.Tab>
          <Tabs.Tab index={1}>Branches ({company.branches.length})</Tabs.Tab>
          <Tabs.Tab index={2}>Domains</Tabs.Tab>
          <Tabs.Tab index={3}>Database</Tabs.Tab>
          <Tabs.Tab index={4}>Settings</Tabs.Tab>
        </Tabs.List>
        <Tabs.Panels className="pt-3">
          <Tabs.Panel index={0}><ProfileTab key={company.slug} company={company} act={act} /></Tabs.Panel>
          <Tabs.Panel index={1}><BranchesTab company={company} act={act} /></Tabs.Panel>
          <Tabs.Panel index={2}><DomainsTab company={company} act={act} /></Tabs.Panel>
          <Tabs.Panel index={3}><DatabaseTab company={company} act={act} onChanged={reload} /></Tabs.Panel>
          <Tabs.Panel index={4}><CompanySettingsTab company={company} act={act} /></Tabs.Panel>
        </Tabs.Panels>
      </Tabs>
    </PageShell>
  );
}
